import { getReviewItemValue } from "@/Helpers/reviewedItems.js";
import i18n from "@/Plugins/i18n";
import { shallowRef } from "vue";
import { router } from "@inertiajs/vue3";
import { MICROTRANSACTION_ITEMS } from "./constants";
import { debounce } from "lodash";
import { usePage } from "@inertiajs/vue3";
import { UserRequests } from "./requests";
import axios from "axios";
import { eventBus } from "@/Helpers/eventbus.js";

const { t } = i18n.global;

/**
 * scrollTo - Horizontal Scrolling
 * @param {(HTMLElement ref)} element - Scroll Container
 * @param {number} scrollPixels - pixel to scroll
 * @param {number} duration -  Duration of scrolling animation in millisec
 */
const scrollTo = (element, scrollPixels, duration) => {
    const scrollPos = element.scrollLeft;
    if (
        !(
            (scrollPos === 0 || scrollPixels > 0) &&
            (element.clientWidth + scrollPos === element.scrollWidth ||
                scrollPixels < 0)
        )
    ) {
        const startTime =
            "now" in window.performance
                ? performance.now()
                : new Date().getTime();

        function scroll(timestamp) {
            const timeElapsed = timestamp - startTime;
            const progress = Math.min(timeElapsed / duration, 1);
            element.scrollLeft = scrollPos + scrollPixels * progress;
            if (timeElapsed < duration) {
                window.requestAnimationFrame(scroll);
            } else {
                return;
            }
        }
        window.requestAnimationFrame(scroll);
    }
};

const scroll = {
    left: (el) => {
        scrollTo(el, -200, 400);
    },
    right: (el) => {
        scrollTo(el, 200, 400);
    },
};

const isScrollable = function (ele) {
    const hasScrollableContent = ele.scrollHeight > ele.clientHeight;
    const overflowYStyle = window.getComputedStyle(ele).overflowY;
    const isOverflowHidden = overflowYStyle.indexOf("hidden") !== -1;

    return hasScrollableContent && !isOverflowHidden;
};

export function importDynamicIcon(props) {
    let icon = props.icon;
    if (!icon) {
        return;
    }

    if (typeof icon !== "string") {
        return icon;
    }

    const currentIcon = shallowRef("");
    import(`../Components/Icons/${icon}.vue`).then((val) => {
        currentIcon.value = val.default;
    });

    return currentIcon;
}

export function storyOf(component, settings = {}) {
    return {
        title: component.__file
            ?.replace("resources/js/", "")
            .replace(".vue", ""),
        component,
        ...settings,
    };
}

export const isCurrentPage = (url) => usePage().url === url;

export const currentPageIncludesString = (string) =>
    window.location.href.includes(string);

export const capitalizeFirstLetter = (string) => {
    if (!string) return;
    return string.charAt(0).toUpperCase() + string.slice(1);
};

export const getUserTitle = (user) => {
    const gender = user?.profile?.gender ?? user?.gender;
    const occupation_key = user?.profile?.occupation ?? user?.occupation;
    const occupation =
        gender && occupation_key
            ? t(`home.profile.occupations.${gender}.${occupation_key}`)
            : null;

    const jobTitle = capitalizeFirstLetter(
        getReviewItemValue(
            user?.profile?.job_title ?? user?.approved_job_title ?? occupation
        )
    );

    const age = user?.profile?.age ?? user?.age;

    return { jobTitle, age, occupation };
};

export const getUserPronoun = (user) => {
    const gender = user?.profile?.gender ?? user?.gender;

    return t(`generic.ls_generic_pronoun_${gender}`);
};

export const getPercentageJudgement = (percentage) => {
    if (percentage > 66) return "positive";
    else if (percentage < 34) return "negative";

    return "neutral";
};

export const getDayTimeTranslation = () => {
    const hours = new Date().getHours();

    if (4 <= hours && hours < 12) {
        return "morning";
    }
    if (12 <= hours && hours < 18) {
        return "day";
    }
    return "evening";
};

export async function copyToClipboard(item) {
    await navigator.clipboard
        .writeText(item)
        .then(() => {
            router.post(
                route("show.toast"),
                { type: "success", message: "toast.success.copied" },
                {
                    preserveScroll: true,
                    preserveState: true,
                }
            );
        })
        .catch(() => {
            router.post(
                route("show.toast"),
                { type: "error", message: "toast.error.copied" },
                {
                    preserveScroll: true,
                    preserveState: true,
                }
            );
        });
}

export function setRandomElementOfArray(array) {
    return array[getRandomIndex(array)];
}

export function getRandomIndex(array) {
    return Math.floor(Math.random() * (array.length - 1));
}

export { scroll, isScrollable };

export const getCharacteristicsLabel = (key) => {
    return t(`user.profile_overview.characteristic.${key}.label`);
};

export const performMicrointeraction = debounce(
    (balance, context, user, partner, data) => {
        const microinteractionItem = MICROTRANSACTION_ITEMS.find(
            (item) => item.context === context
        );

        if (context === "exclusive_message" && !user.is_premium) {
            UserRequests.BannerRedirected({
                banner: "PremiumExclusiveMessageModal",
                data: context,
            });

            return;
        }

        if (
            !balance ||
            (microinteractionItem && microinteractionItem.price > balance)
        ) {
            UserRequests.BannerRedirected({
                banner: "MissingCoinBalanceMinimizedModal",
                data: context,
                partner: partner,
            });

            return;
        }

        if (
            context === "exclusive_message" &&
            usePage().url.includes("partner") &&
            usePage().url.includes("profile")
        ) {
            router.get(
                route("inbox.messenger.show", {
                    partner_id: partner.unique_identifier,
                })
            );

            eventBus.flash.inboxTrigger = "exclusive_message";

            return;
        }

        if (
            route().current() === "inbox.messenger.show" ||
            route().current() === "inbox.messenger.show.hidden"
        ) {
            eventBus.pushMessage("", context, data);
            axios
                .post(
                    route(
                        "partner.perform.microtransaction",
                        partner.unique_identifier
                    ),
                    {
                        context: context,
                        data: data,
                    }
                )
                .then((response) => {
                    eventBus.flash.coins = response.data.coins;
                });
        } else {
            router.post(
                route(
                    "partner.perform.microtransaction",
                    partner.unique_identifier
                ),
                { context: context, data: data },
                {
                    preserveState: true,
                    preserveScroll: true,
                }
            );
        }
    },
    2000,
    { leading: true, trailing: false }
);

export const getRedirectUrl = (path) => {
    const appEnvironment = usePage().props.environment;

    switch (appEnvironment) {
        case "production":
            return "https://lemonswan.com" + path;
        case "staging":
            return "https://staging.lemonswan.com" + path;
        default:
            return "http://lemonswan.com.test" + path;
    }
};
